import React from 'react'
import PropTypes from 'prop-types'
import { format, isValid } from 'date-fns'
import {
  Button,
  Icon,
  Flex,
  Text,
  Menu,
  MenuList,
  MenuButton,
  MenuOptionGroup,
  MenuItemOption,
  Portal,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  useDisclosure,
  useControllableState,
} from '@chakra-ui/react'
import FiChevronUp from '@meronex/icons/fi/FiChevronUp'
import FiChevronDown from '@meronex/icons/fi/FiChevronDown'

import { DateRangePicker } from '../date-range-picker'

const defaultLocales = { Placeholder: 'Dates' }

export const DateRangeFilter = ({ options, defaultValue, value: valueProp, min, max, onChange, formatter, locales: localesProp }) => {
  const { isOpen: isRangePickerOpen, onOpen: openRangePicker, onClose: closeRangePicker } = useDisclosure()
  const [value, setValue] = useControllableState({ defaultValue, value: valueProp, onChange })
  const locales = React.useMemo(() => ({ ...defaultLocales, ...localesProp }), [localesProp])
  const selectedRange = React.useMemo(() => options.find((option) => option.label === value.label), [options, value])

  const onOptionGroupChange = React.useCallback(
    (label) => {
      const option = options.find((option) => option.label === label)

      if (isValid(option.start) && isValid(option.end)) {
        setValue(option)
      } else {
        openRangePicker()
      }
    },
    [options, setValue, openRangePicker],
  )
  const onRangePickerConfirm = React.useCallback(
    (event, { start, end }) => {
      const option = options.find((option) => !isValid(option.start) && !isValid(option.end))
      const value = { label: option.label, start, end }

      closeRangePicker()
      setValue(value)
    },
    [options, setValue, closeRangePicker],
  )

  return (
    <>
      <Menu placement='bottom-end'>
        {({ isOpen }) => (
          <>
            <MenuButton
              as={Button}
              isActive={isOpen}
              rightIcon={<Icon as={isOpen ? FiChevronUp : FiChevronDown} boxSize='1rem' />}
              size='sm'
              colorScheme='gray'
            >
              <Flex>
                {locales.Placeholder}
                {typeof selectedRange !== 'undefined' && (
                  <>
                    :
                    <Text marginLeft='0.25rem'>
                      {isValid(selectedRange.start) && isValid(selectedRange.end)
                        ? selectedRange.label
                        : `${formatter(value.start)} - ${formatter(value.end)}`}
                    </Text>
                  </>
                )}
              </Flex>
            </MenuButton>

            <Portal>
              <MenuList paddingY='0.5rem' minWidth='10rem'>
                {/* value accept string type only */}
                <MenuOptionGroup type='radio' value={value.label} onChange={onOptionGroupChange}>
                  {options.map((option, index) => (
                    <MenuItemOption key={index} fontSize='sm' value={option.label}>
                      {option.label}
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>
              </MenuList>
            </Portal>
          </>
        )}
      </Menu>

      <Modal size='3xl' isOpen={isRangePickerOpen} onClose={closeRangePicker}>
        <ModalOverlay />
        <ModalContent>
          <ModalBody padding='0'>
            <DateRangePicker min={min} max={max} onConfirm={onRangePickerConfirm} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

const OptionShape = PropTypes.shape({ label: PropTypes.string, start: PropTypes.instanceOf(Date), end: PropTypes.instanceOf(Date) })
DateRangeFilter.propTypes = {
  options: PropTypes.arrayOf(OptionShape).isRequired,
  defaultValue: OptionShape,
  value: OptionShape,
  min: PropTypes.instanceOf(Date),
  max: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
  formatter: PropTypes.func,
  locales: PropTypes.shape({ Placeholder: PropTypes.string }),
}

DateRangeFilter.defaultProps = {
  defaultValue: { label: '', start: null, end: null },
  formatter: (date) => format(date, 'MMMM d, yyyy'),
}
